// Super Admin → RF Log. Every code any RF receiver heard, estate-wide, with a
// date range and filters — and, on top, the transmitters heard in that range
// that nobody has configured yet, each with a Configure button. That top list
// is "what was installed today": press a new sensor once and it is here.
//
// Rows: core/devices/rfLog (GetRfEventLog, From/To since 2026-10-12). Configure
// opens the normal sensor dialog (RfSensorDialog) on the sensor row the server
// created when it first heard the transmitter — the same dialog the receiver's
// own screen uses, so nothing about configuring differs here.
//
// ⚠️ THE SERVER RUNS RF ACTIONS. This page only reads the log and names sensors;
// it never publishes (see RfDevicesPanel for why a second publish is harmful).
// ⚠️ GATED AT THE ROUTE AND HERE, like every admin tool.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, AppBar, Box, Button, Card, Chip, CircularProgress, IconButton, InputAdornment,
  MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Toolbar, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { useApp } from '../../../core/state/AppContext';
import { getRfDevices, getRfEventLog } from '../../../core/api/api';
import {
  rfLogRowOf, filterRfLog, rfLogOptions, unconfiguredTransmitters, ymd,
} from '../../../core/devices/rfLog';
import { loadSignatures, refreshSignatures } from '../../../core/devices/rfSignatures';
import { rfTypeLabel, rfTypeIcon } from '../../../core/devices/rfTypes';
import { invalidateRfDirectory } from '../../../core/hooks/useRfDirectory';
import RfSensorDialog from '../../shared/RfSensorDialog';

const LIMIT = 5000;
const DAY_MS = 86400000;

const when = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }));

function Pick({ label, value, onChange, options, width = 170 }) {
  return (
    <TextField select size="small" label={label} value={value} onChange={(e) => onChange(e.target.value)} sx={{ width }}>
      <MenuItem value="">All</MenuItem>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>{`${o.label} (${o.count})`}</MenuItem>
      ))}
    </TextField>
  );
}

export default function RfLogScreen() {
  const { isSuperAdmin } = useApp();
  const navigate = useNavigate();
  useEffect(() => { if (!isSuperAdmin) navigate('/home', { replace: true }); }, [isSuperAdmin, navigate]);

  const today = ymd();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [rows, setRows] = useState(null);       // null = loading
  const [error, setError] = useState('');
  const [sig, setSig] = useState(null);
  const [filters, setFilters] = useState({
    search: '', receiver: '', location: '', type: '', meaning: '', status: '',
  });
  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  const [editing, setEditing] = useState(null);  // { device, locationId }
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    if (!from || !to || from > to) return;
    setRows(null);
    setError('');
    // Days too, for a server older than From/To: it then returns "the last N
    // days" and the range is applied here instead.
    const days = Math.min(365, Math.max(1, Math.ceil((Date.now() - new Date(`${from}T00:00:00`).getTime()) / DAY_MS) + 1));
    try {
      const res = await getRfEventLog({ From: from, To: to, Days: days, Limit: LIMIT });
      const list = Array.isArray(res?.data) ? res.data : [];
      const lo = new Date(`${from}T00:00:00`).getTime();
      const hi = new Date(`${to}T00:00:00`).getTime() + DAY_MS;
      setRows(list.map(rfLogRowOf).filter((r) => r.at == null || (r.at >= lo && r.at < hi)));
    } catch (e) {
      setError('Could not read the RF log. Is the backend deployed?');
      setRows([]);
    }
  }, [from, to]);

  useEffect(() => { if (isSuperAdmin) load(); }, [isSuperAdmin, load]);
  useEffect(() => { if (isSuperAdmin) loadSignatures().then(setSig).catch(() => {}); }, [isSuperAdmin]);

  const shown = useMemo(() => filterRfLog(rows || [], filters), [rows, filters]);
  // The "new" list follows every filter except Status (it IS the unconfigured view).
  const pending = useMemo(
    () => unconfiguredTransmitters(filterRfLog(rows || [], { ...filters, status: '' }), sig),
    [rows, filters, sig],
  );
  const opt = useMemo(() => ({
    receiver: rfLogOptions(rows, 'receiver'),
    location: rfLogOptions(rows, 'location'),
    type: rfLogOptions(rows, 'type'),
    meaning: rfLogOptions(rows, 'meaning'),
  }), [rows]);

  // The sensor row the server created on first hearing the transmitter, with
  // every code heard in this range attached, so the dialog can name them.
  const configure = async ({ rfDeviceId, transmitterId, codes = [], receiverUno = '', locationId = 0 }) => {
    setNote('');
    const res = await getRfDevices({}).catch(() => null);
    const list = Array.isArray(res?.data) ? res.data : [];
    const found = list.find((d) => (rfDeviceId && Number(d.RfDeviceId) === rfDeviceId))
      || list.find((d) => Number(d.TransmitterId) === transmitterId);
    if (!found) {
      setNote(`No sensor row for transmitter ${transmitterId} yet — open its receiver and use Scan / Add.`);
      return;
    }
    const have = new Set((found.Codes || []).map((c) => String(c.REMOTEVALUE)));
    const extra = codes.filter((c) => !have.has(String(c))).map((c) => ({
      REMOTEVALUE: String(c), DeviceAction: '', UniqueNumber: receiverUno,
    }));
    setEditing({
      device: { ...found, Codes: [...(found.Codes || []), ...extra] },
      locationId: Number(found.LocationID) || locationId || 0,
    });
  };

  const quick = (daysBack) => {
    const d = new Date(Date.now() - daysBack * DAY_MS);
    setFrom(ymd(d));
    setTo(daysBack === 1 ? ymd(d) : today);
  };

  if (!isSuperAdmin) return null;

  return (
    <Box>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}><ArrowBackIcon /></IconButton>
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>RF Log</Typography>
          <Tooltip title="Reload">
            <IconButton color="inherit" onClick={load}><RefreshIcon /></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {/* ── Filters ── */}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
          <TextField size="small" type="date" label="From" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ max: to }} />
          <TextField size="small" type="date" label="To" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ min: from, max: today }} />
          <Chip label="Today" onClick={() => quick(0)} color={from === today && to === today ? 'primary' : 'default'} />
          <Chip label="Yesterday" onClick={() => quick(1)} />
          <Chip label="Last 7 days" onClick={() => quick(6)} />
          <TextField
            select size="small" label="Status" value={filters.status} onChange={(e) => setF('status')(e.target.value)} sx={{ width: 170 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="new">Not configured</MenuItem>
            <MenuItem value="configured">Configured</MenuItem>
          </TextField>
          <Pick label="Location" value={filters.location} onChange={setF('location')} options={opt.location} />
          <Pick label="Receiver" value={filters.receiver} onChange={setF('receiver')} options={opt.receiver} width={210} />
          <Pick label="Type" value={filters.type} onChange={setF('type')} options={opt.type} width={140} />
          <Pick label="Event" value={filters.meaning} onChange={setF('meaning')} options={opt.meaning} width={140} />
          <TextField
            size="small" placeholder="Name / RF no. / code / hw id…" value={filters.search}
            onChange={(e) => setF('search')(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 240 }}
          />
          {Object.values(filters).some(Boolean) && (
            <Button size="small" onClick={() => setFilters({ search: '', receiver: '', location: '', type: '', meaning: '', status: '' })}>
              Clear filters
            </Button>
          )}
        </Stack>

        {from > to && <Alert severity="warning" sx={{ mb: 1.5 }}>“From” is after “To”.</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {note && <Alert severity="info" sx={{ mb: 1.5 }} onClose={() => setNote('')}>{note}</Alert>}
        {rows && rows.length >= LIMIT && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {`Only the latest ${LIMIT} codes in this range are shown — narrow the dates to see older ones.`}
          </Alert>
        )}

        {rows == null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <>
            {/* ── Not configured yet ── */}
            {filters.status !== 'configured' && (
              <Card sx={{ mb: 2, borderRadius: 2, border: 1, borderColor: pending.length ? 'success.main' : 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
                  <FiberNewIcon color={pending.length ? 'success' : 'disabled'} />
                  <Typography variant="subtitle1" fontWeight={700}>
                    {`Not configured yet — ${pending.length} transmitter${pending.length === 1 ? '' : 's'}`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">heard in this range, no name yet</Typography>
                </Box>
                {pending.length > 0 && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['RF no.', 'Hw id', 'Probably', 'Codes heard', 'Presses', 'First heard', 'Last heard', 'Receiver', 'Location', ''].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pending.map((t) => (
                          <TableRow key={t.transmitterId} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.rfNumber || '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t.transmitterId}</TableCell>
                            <TableCell>{t.guessType ? `${rfTypeIcon(t.guessType)} ${rfTypeLabel(t.guessType)}?` : '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {t.eventBytes.map((b) => `#${b}`).join(' ')}
                            </TableCell>
                            <TableCell align="center">{t.count}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(t.firstAt)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(t.lastAt)}</TableCell>
                            <TableCell>{t.receivers.map((r) => r.name).join(', ') || '—'}</TableCell>
                            <TableCell>{t.locationName || '—'}</TableCell>
                            <TableCell>
                              <Button
                                size="small" variant="contained"
                                onClick={() => configure({
                                  rfDeviceId: t.rfDeviceId,
                                  transmitterId: t.transmitterId,
                                  codes: t.codes,
                                  receiverUno: t.receivers[0]?.uno || '',
                                  locationId: t.locationId,
                                })}
                              >
                                Configure
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Card>
            )}

            {/* ── Every code ── */}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              {`${shown.length} of ${rows.length} codes · click a row to open its sensor`}
            </Typography>
            <TableContainer component={Card} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', maxHeight: '65vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Time', 'Sensor', 'Type', 'Event', 'Code', 'Receiver', 'Location', 'Room', 'Actions', 'Told'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map((r) => (
                    <TableRow
                      key={r.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => configure({
                        rfDeviceId: r.rfDeviceId,
                        transmitterId: r.transmitterId,
                        codes: [r.code],
                        receiverUno: r.receiverUno,
                        locationId: r.locationId,
                      })}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(r.at)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {r.configured
                          ? <><b>{r.name}</b> <Typography component="span" variant="caption" color="text.secondary">{r.rfNumber}</Typography></>
                          : <Chip size="small" color="success" variant="outlined" label={`New ${r.rfNumber || `hw ${r.transmitterId}`}`} />}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.rfType ? `${rfTypeIcon(r.rfType)} ${rfTypeLabel(r.rfType)}` : '—'}</TableCell>
                      <TableCell>{r.meaning || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {`${r.code} · #${r.eventByte}`}
                      </TableCell>
                      <TableCell>{r.receiverName || r.receiverUno || '—'}</TableCell>
                      <TableCell>{r.locationName || '—'}</TableCell>
                      <TableCell>{r.roomName || '—'}</TableCell>
                      <TableCell align="center">{r.actionsFired || ''}</TableCell>
                      <TableCell align="center">{r.notified || ''}</TableCell>
                    </TableRow>
                  ))}
                  {shown.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {rows.length ? 'Nothing matches the filters.' : 'No RF codes were heard in this range.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      <RfSensorDialog
        device={editing?.device || null}
        locationId={editing?.locationId || 0}
        onClose={() => setEditing(null)}
        onSaved={() => { invalidateRfDirectory(); refreshSignatures().then(setSig).catch(() => {}); load(); }}
      />
    </Box>
  );
}
